// Helpers for the client page (Advertisers/Clients -> click a client).
// Pure, no React, so they can be tested with node.

export const BOOKING_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'past', label: 'Past' },
  { value: 'uninvoiced', label: 'Not invoiced' },
];

const pad = (n) => String(n).padStart(2, '0');
export const todayKey = (d = new Date()) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// "full_page" -> "Full Page"; names that are already readable stay as they are.
export const productLabel = (value) => {
  if (!value) return '';
  if (!value.includes('_')) return value;
  return value.split('_').filter(Boolean).map(w => w[0].toUpperCase() + w.slice(1)).join(' ');
};

export const statusLabel = (value) => productLabel(value || '') || '—';

// What a booking is, in one line: edition (print) or dates (digital).
export const bookingWhere = (b) => {
  if (!b) return '';
  if (b.category === 'website') {
    const start = b.digital_start_date;
    const end = b.digital_end_date;
    if (!start) return 'Digital · not scheduled';
    return end && end !== start ? `Digital · ${start} → ${end}` : `Digital · ${start}`;
  }
  return b.edition_name || 'Print';
};

export const filterBookings = (bookings, filter, today = todayKey()) => {
  const list = bookings || [];
  if (filter === 'upcoming') return list.filter(b => b.status !== 'cancelled' && (b.run_date || '') >= today);
  if (filter === 'past') return list.filter(b => b.run_date && b.run_date < today);
  if (filter === 'uninvoiced') return list.filter(b => b.status !== 'cancelled' && !b.invoice_id);
  return list;
};

export const invoiceNumbers = (invoices) =>
  Object.fromEntries((invoices || []).map(i => [i.id, i.invoice_number]));

// Every payment on file, newest first: receipts recorded in Accounting, and
// invoices marked paid (online through Stripe, or by hand) that have no
// receipt of their own.
export const paymentsFrom = (invoices, receipts) => {
  const rows = [];
  const withReceipt = new Set();
  (receipts || []).forEach(r => {
    (r.invoice_ids || []).forEach(id => withReceipt.add(id));
    rows.push({
      key: `r-${r.id}`,
      kind: 'receipt',
      date: r.payment_date || (r.created_at || '').slice(0, 10),
      reference: r.receipt_number || 'Receipt',
      method: r.payment_method || '',
      amount: Number(r.amount_paid) || 0,
      currency: r.currency || 'USD',
      invoices: (r.invoice_lines || []).map(l => l.invoice_number).filter(Boolean),
      balance: Number(r.balance) || 0,
      item: r,
    });
  });
  (invoices || []).forEach(inv => {
    if (inv.status !== 'paid' || withReceipt.has(inv.id)) return;
    const online = !!inv.stripe_payment_intent || inv.stripe_amount_paid != null;
    const mxn = inv.paid_currency === 'MXN' && inv.paid_amount_original != null;
    rows.push({
      key: `i-${inv.id}`,
      kind: online ? 'online' : 'marked',
      date: (inv.paid_at || '').slice(0, 10),
      reference: inv.invoice_number,
      method: online ? 'Card (online)' : 'Marked paid',
      amount: mxn ? Number(inv.paid_amount_original) : Number(inv.stripe_amount_paid ?? inv.total) || 0,
      currency: mxn ? 'MXN' : 'USD',
      invoices: [inv.invoice_number],
      balance: 0,
      item: inv,
    });
  });
  return rows.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
};

export const money = (amount, currency = 'USD') => {
  const n = Number(amount) || 0;
  const s = n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency === 'MXN' ? `MX$${s}` : `$${s}`;
};

// Editions an invoice bills, from its own line items.
export const invoiceEditions = (inv) => {
  const names = ((inv && inv.line_items) || [])
    .map(l => l.edition_name)
    .filter(n => n && n !== 'N/A');
  return [...new Set(names)];
};

// How an invoice got paid, in a few words. '' while unpaid.
export const paidHow = (inv, receipts) => {
  if (!inv || inv.status !== 'paid') return '';
  const r = (receipts || []).find(x => (x.invoice_ids || []).includes(inv.id));
  if (r) return `Receipt ${r.receipt_number || ''}`.trim();
  if (inv.stripe_payment_intent || inv.stripe_amount_paid != null) return `Card online (${inv.paid_currency || 'USD'})`;
  return 'Marked paid';
};

// Package names used in an order, from its bookings.
export const orderPackages = (orderId, bookings) => [...new Set(
  (bookings || []).filter(b => b.order_id === orderId).map(b => b.order_line_package_name).filter(Boolean),
)];

// The files on an activity as [{ url, name }]. Activities from before several
// files were allowed keep theirs in attachment_url / attachment_name.
export const activityFiles = (a) => {
  if (Array.isArray(a?.attachments) && a.attachments.length) return a.attachments;
  return a?.attachment_url ? [{ url: a.attachment_url, name: a.attachment_name || '' }] : [];
};

// Client history: does this line have to do with a proposal?
export const isProposalEvent = (ev) => {
  if (ev?.kind !== 'activity') return false;
  const a = ev.item || {};
  return activityFiles(a).length > 0 || a.activity_type === 'proposal_shown' || a.outcome === 'proposal_sent';
};

// The line about a client's contract. today is YYYY-MM-DD.
export const contractState = (endDate, today) => {
  if (!endDate) return null;
  return endDate >= today ? 'active' : 'ended';
};
